// Configurable free gifts.
// Each enabled gift is auto-added when cart subtotal (excluding all gift line items) >= its threshold
// AND none of its excluded products are in the cart. Removed when either condition stops being true.
// Configuration is injected via window.FreeGiftConfig (see layout/theme.liquid).
(function () {
  'use strict';

  const DEBOUNCE_MS = 150;
  const CART_MUTATION_RE = /\/cart\/(add|change|update|clear)\.js(\?|$)/;
  const BUSY_CLASS = 'free-gift-busy';
  const CHECKOUT_SELECTOR = '[name="checkout"]';

  const log = (...args) => {
    if (window.FreeGiftDebug) console.debug('[FreeGift]', ...args);
  };

  const rawGifts = (window.FreeGiftConfig && window.FreeGiftConfig.gifts) || [];
  const gifts = rawGifts.filter((g) => g && g.enabled && g.variantId);
  if (gifts.length === 0) {
    log('No enabled gifts configured — exiting.');
    return;
  }
  const giftVariantIds = new Set(gifts.map((g) => g.variantId));
  log('Active gifts:', gifts);

  let inFlight = false;
  let pendingReconcile = false;
  let debounceTimer = null;

  // Inject CSS to visually disable checkout buttons while the cart is unsettled.
  const style = document.createElement('style');
  style.textContent = `
    body.${BUSY_CLASS} ${CHECKOUT_SELECTOR} {
      pointer-events: none !important;
      opacity: 0.5 !important;
      cursor: wait !important;
    }
  `;
  (document.head || document.documentElement).appendChild(style);

  function setBusy(busy) {
    document.body && document.body.classList.toggle(BUSY_CLASS, busy);
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((btn) => {
      btn.disabled = busy;
      if (busy) btn.setAttribute('aria-busy', 'true');
      else btn.removeAttribute('aria-busy');
    });
  }

  function scheduleReconcile(fromMutation) {
    if (fromMutation) setBusy(true);
    if (inFlight) {
      pendingReconcile = true;
      return;
    }
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runReconcile, DEBOUNCE_MS);
  }

  async function runReconcile() {
    debounceTimer = null;
    await reconcile();
    if (debounceTimer === null && !inFlight && !pendingReconcile) setBusy(false);
  }

  async function flushReconcile() {
    clearTimeout(debounceTimer);
    debounceTimer = null;
    while (inFlight) {
      await new Promise((r) => setTimeout(r, 20));
    }
    pendingReconcile = false;
    await reconcile();
    while (pendingReconcile) {
      pendingReconcile = false;
      await reconcile();
    }
  }

  async function reconcile() {
    if (inFlight) return;
    inFlight = true;
    try {
      const res = await fetch('/cart.js', {credentials: 'same-origin'});
      if (!res.ok) {
        log('GET /cart.js failed:', res.status);
        return;
      }
      const cart = await res.json();

      // Compute subtotal once, excluding ALL configured gift line items. This way an active
      // gift's own price (typically 0) never inflates another gift's threshold check.
      const giftLinesTotal = cart.items
        .filter((i) => giftVariantIds.has(i.variant_id))
        .reduce((sum, i) => sum + i.final_line_price, 0);
      const subtotalWithoutGifts = cart.items_subtotal_price - giftLinesTotal;

      let mutated = false;
      for (const gift of gifts) {
        const giftLine = cart.items.find((i) => i.variant_id === gift.variantId);
        const hasExcluded = cart.items.some((i) => gift.excludedProductIds.includes(i.product_id));
        const shouldHaveGift = subtotalWithoutGifts >= gift.thresholdCents && !hasExcluded;

        log({
          variantId: gift.variantId,
          subtotalWithoutGifts,
          threshold: gift.thresholdCents,
          hasExcluded,
          giftInCart: !!giftLine,
          shouldHaveGift,
        });

        if (shouldHaveGift && !giftLine) {
          if (await addGift(gift.variantId)) mutated = true;
        } else if (shouldHaveGift && giftLine && giftLine.quantity !== 1) {
          if (await changeQty(giftLine.key, 1)) mutated = true;
        } else if (!shouldHaveGift && giftLine) {
          if (await changeQty(giftLine.key, 0)) mutated = true;
        }
      }

      if (mutated) {
        document.dispatchEvent(new CustomEvent('apps:product-added-to-cart', {detail: {}}));
      }
    } catch (e) {
      log('Reconcile error:', e);
    } finally {
      inFlight = false;
      if (pendingReconcile) {
        pendingReconcile = false;
        scheduleReconcile(true);
      }
    }
  }

  async function addGift(variantId) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({id: variantId, quantity: 1}),
      });
      if (!res.ok) log('addGift failed:', variantId, res.status);
      return res.ok;
    } catch (e) {
      log('addGift error:', variantId, e);
      return false;
    }
  }

  async function changeQty(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({id: key, quantity}),
      });
      if (!res.ok) log('changeQty failed:', res.status);
      return res.ok;
    } catch (e) {
      log('changeQty error:', e);
      return false;
    }
  }

  // Re-apply disabled state whenever the cart drawer re-renders mid-reconcile
  // (the theme replaces innerHTML with fresh buttons that lose our disabled attribute).
  const reapplyObserver = new MutationObserver(() => {
    if (document.body && document.body.classList.contains(BUSY_CLASS)) {
      document.querySelectorAll(CHECKOUT_SELECTOR).forEach((btn) => {
        if (!btn.disabled) {
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
        }
      });
    }
  });
  const startObserver = () => {
    if (document.body) reapplyObserver.observe(document.body, {childList: true, subtree: true});
  };
  if (document.body) startObserver();
  else document.addEventListener('DOMContentLoaded', startObserver, {once: true});

  // Trigger 1: initial page load (no busy state — cart is assumed stable on first load).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleReconcile(false), {once: true});
  } else {
    scheduleReconcile(false);
  }

  // Trigger 2: theme's cart:updated event — real mutation, block checkout.
  document.addEventListener('cart:updated', () => scheduleReconcile(true));

  // Safety net: if user still beats the block, flush before letting checkout submit.
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const submitter = e.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      if (form.dataset._giftFlushed === '1') {
        delete form.dataset._giftFlushed;
        return;
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      setBusy(true);
      flushReconcile().finally(() => {
        setBusy(false);
        form.dataset._giftFlushed = '1';
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submitter);
        } else {
          form.submit();
        }
      });
    },
    true,
  );

  // Trigger 3: intercept any successful cart mutation fetch.
  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const promise = originalFetch.apply(this, args);
    const url = args[0] instanceof Request ? args[0].url : String(args[0] || '');
    if (CART_MUTATION_RE.test(url)) {
      setBusy(true);
      promise
        .then((res) => {
          if (res && res.ok) scheduleReconcile(true);
          else if (debounceTimer === null && !inFlight && !pendingReconcile) setBusy(false);
        })
        .catch(() => {
          if (debounceTimer === null && !inFlight && !pendingReconcile) setBusy(false);
        });
    }
    return promise;
  };
})();
